// Imagen por defecto para proyectos sin backgroundImage: persona viendo proyectos
// inmobiliarios en el celular (foto, mismo tratamiento apagado que las fotos de proyecto).
import DefaultHeroPic from '../../../assets/plr_default_hero.webp';

type HeroOptions = {
  backgroundImage?: string;
  hideHero?: boolean;
  heroQuery?: string | null;
  partnerName?: string;
};

export const PLR_HERO_FALLBACK_IMAGE = DefaultHeroPic;
export const PLR_RESULT_TIME = 'Resultado en cerca de 3 minutos';
export const PLR_ALLIANCE_COPY = 'Evaluación en alianza con banca.me';

export const resolvePlrHeroImage = (backgroundImage?: string) => (
  backgroundImage || PLR_HERO_FALLBACK_IMAGE
);

// Solo los productos inmobiliarios tienen proyecto: en crédito de consumo (p. ej. "Bancame DPP")
// los textos no dicen "este proyecto".
export const isMortgageFlow = (products?: readonly string[]) => Boolean(
  products?.some((product) => product === 'mortgage' || product === 'downPayment'),
);

// Nombres de entidad internos, que no son un proyecto (revisión de prod, 2 nov 2026). En su lugar
// se muestra el nombre del partner (sin sobrelínea), "Evalúate con nosotros" y "Comenzar".
const INTERNAL_ENTITY_PATTERNS = [
  /-\s*default$/i, // entidad por defecto de cada partner ("<Partner>-default")
  /^brokers$/i, // canal de corredores (Inmobiliaria PY, Ecasa)
  /^cyber\b/i, // campañas ("Cyber Octubre 2026", Euro Inmobiliaria)
  /^mutuaria\b/i, // líneas de producto de mutuarias ("Mutuaria Andes - Con subsidio")
  /\bprueba\b/i, // entidades de prueba ("Enlace Prueba")
];
const INTERNAL_ENTITY_NAMES = new Set([
  'lecaros group-comercial', 'compraventa usados', 'piloto portal arriendo', // Lecaros Group
  'clientes evaluados - equipo credito', // Euro Inmobiliaria
  'principal', // Inmobiliaria Ciclos
  'perfil mutuaria', // Nash
  'inversiones', // Socovesa
  'servicio financiamiento pies', // Brick
  'inovo', // Ecasa (corredor)
  'medicosinvirtiendo', // Medicos Invirtiendo
  // Activa Inmobiliaria: una entidad por vendedor.
  'camila', 'mariasoledad', 'jessica', 'adriana', 'francisco valdivieso', 'yetzi',
]);

export const isInternalEntityName = (name: string) => {
  const entity = name.trim();
  return INTERNAL_ENTITY_PATTERNS.some((pattern) => pattern.test(entity))
    || INTERNAL_ENTITY_NAMES.has(entity.toLowerCase());
};

// Hay un proyecto con nombre: una entidad que no es interna, de un partner inmobiliario (sin
// `products` no se descarta por producto). Los textos pueden decir "este proyecto".
export const hasProjectName = (entityName?: string, products?: readonly string[]) => {
  const entity = entityName?.trim();
  if (!entity || isInternalEntityName(entity)) return false;
  return products === undefined || isMortgageFlow(products);
};

export const projectEntityLabel = (
  entityName?: string,
  partnerName?: string,
  products?: readonly string[],
) => (
  hasProjectName(entityName, products) ? (entityName as string).trim() : partnerName?.trim() || ''
);

export const companyOverline = (
  entityName?: string,
  partnerName?: string,
  products?: readonly string[],
) => {
  const company = partnerName?.trim();
  const project = projectEntityLabel(entityName, partnerName, products);
  if (!company || company === project) return undefined;
  return company;
};

// Atajos para el formSchema de los pasos (partner = nombre del partner) y para el Partner
// del shell.
type SchemaEntity = {
  partnerEntityName?: string,
  partner?: string,
  products?: readonly string[],
};
type PartnerEntity = {
  partnerEntityName?: string,
  partnerName?: string,
  products?: readonly string[],
};
export const schemaProjectLabel = (schema: SchemaEntity) => projectEntityLabel(
  schema.partnerEntityName,
  schema.partner,
  schema.products,
);
export const schemaHasProject = (schema: SchemaEntity) => hasProjectName(
  schema.partnerEntityName,
  schema.products,
);
export const partnerProjectLabel = (partner: PartnerEntity) => projectEntityLabel(
  partner.partnerEntityName,
  partner.partnerName,
  partner.products,
);
export const partnerCompanyOverline = (partner: PartnerEntity) => companyOverline(
  partner.partnerEntityName,
  partner.partnerName,
  partner.products,
);

const shouldShowPlrHero = (options: HeroOptions): boolean => {
  if (options.hideHero) return false;
  if (options.heroQuery === '0' || options.heroQuery === 'false') return false;
  const name = options.partnerName?.toLowerCase();
  if (name === 'banca.me') return false;
  return true;
};

export default shouldShowPlrHero;
